import React from 'react';

import { classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ActionMenu, ActionMenuProps } from '@lumapps/lumx-menu/components/ActionMenu';
import { mdiArrowDown, mdiArrowUp, mdiTrashCanOutline, mdiPencilOutline } from '@lumapps/lumx/icons';
import { Alignment, Emphasis, FlexBox, IconButton, LinkProps, Orientation, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { Direction } from '@lumapps/utils/types/Direction';

import './index.scss';

const CLASSNAME = 'sortable-list-node';

export interface SortableListNodeProps<T> {
    /** additional elements after default actions */
    actions?: React.ReactNode;
    /** Additional actions in contextual menu */
    additionalActions?: React.ReactNode;
    /** Component that will be displayed with the sortable list */
    children?: React.ReactNode;
    /** Custom class name */
    className?: string;
    /** Whether current node is first of the list */
    isFirst?: boolean;
    /** Whether current node is last of the list */
    isLast?: boolean;
    /** Disable the move up button */
    disableMoveUp?: boolean;
    /** Disable the move down button */
    disableMoveDown?: boolean;
    /** Adds a link before default actions */
    linkProps?: LinkProps & { label: string; icon: string };
    /** node to display */
    node: T;
    /** callback to be executed once the node is deleted. Not displayed if prop is undefined. */
    onDeleteNode?(event: React.SyntheticEvent): void;
    /** callback to be executed once the node is edited. Not displayed if prop is undefined. */
    onEditNode?(event: React.SyntheticEvent): void;
    /** callback to be executed once the node is moved in a specific direction. Not displayed if prop is undefined. */
    onMoveNode?(node: T, direction: Direction, event: React.SyntheticEvent): void;
    /** Nested nodes path
     *
     * @default ['0'] for non-nested nodes
     */
    path?: string[];
    /** scope to be used for tracking purposes */
    scope: string;
    /** Handles the default styling
     *
     * @default true
     */
    withStyles?: boolean;
    /** Additional props to set on the action menu */
    actionMenuProps?: Partial<ActionMenuProps>;
    /** Whether an icon should be displayed next to default actions (pencil for edit, trashcan for remove) */
    defaultActionsWithIcon?: boolean;
    /** Translation key for delete option. To use if you do not want to have "Remove" */
    deleteLabel?: string;
}

/**
 * Component primarily used to move it up and down in a list
 *
 * More actions are available in a contextual menu (edit, delete). Other actions can be added
 * through props
 *
 * @family Trees
 */
export const SortableListNode = <T,>({
    actions,
    additionalActions,
    children,
    className,
    withStyles = true,
    isFirst = false,
    isLast = false,
    disableMoveUp = false,
    disableMoveDown = false,
    linkProps,
    node,
    onDeleteNode,
    onEditNode,
    onMoveNode,
    scope,
    actionMenuProps,
    defaultActionsWithIcon = false,
    deleteLabel = GLOBAL.REMOVE,
}: SortableListNodeProps<T>) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(scope);

    const canShowContextualMenu = onEditNode || onDeleteNode || additionalActions;

    return (
        <FlexBox
            className={classnames(CLASSNAME, className, {
                [`${CLASSNAME}--with-styles`]: withStyles,
            })}
            hAlign={Alignment.center}
            orientation={Orientation.horizontal}
        >
            {children}
            <FlexBox orientation={Orientation.horizontal} marginAuto={Alignment.left}>
                {linkProps ? (
                    <IconButton
                        {...linkProps}
                        target="_blank"
                        emphasis={Emphasis.low}
                        size={Size.m}
                        {...get({
                            element: 'item',
                            action: 'view',
                        })}
                    />
                ) : null}

                {onMoveNode && (
                    <>
                        <IconButton
                            label={translateKey(GLOBAL.MOVE_UP)}
                            icon={mdiArrowUp}
                            emphasis={Emphasis.low}
                            size={Size.m}
                            isDisabled={isFirst || disableMoveUp}
                            onClick={(event: React.SyntheticEvent) => onMoveNode(node, Direction.UP, event)}
                            {...get({
                                element: 'item',
                                action: 'move-up',
                            })}
                        />
                        <IconButton
                            label={translateKey(GLOBAL.MOVE_DOWN)}
                            icon={mdiArrowDown}
                            emphasis={Emphasis.low}
                            isDisabled={isLast || disableMoveDown}
                            size={Size.m}
                            onClick={(event: React.SyntheticEvent) => onMoveNode(node, Direction.DOWN, event)}
                            {...get({
                                element: 'item',
                                action: 'move-down',
                            })}
                        />
                    </>
                )}
                {canShowContextualMenu && (
                    <ActionMenu
                        label={translateKey(GLOBAL.MORE)}
                        {...get({
                            element: 'item',
                            action: 'options',
                        })}
                        {...actionMenuProps}
                    >
                        {onEditNode && (
                            <ActionMenu.Item
                                onClick={(event: React.SyntheticEvent) => onEditNode(event)}
                                icon={defaultActionsWithIcon ? mdiPencilOutline : undefined}
                                {...get({
                                    element: 'item',
                                    action: 'edit',
                                })}
                            >
                                {translateKey(GLOBAL.EDIT)}
                            </ActionMenu.Item>
                        )}
                        {additionalActions}
                        {onDeleteNode && (
                            <ActionMenu.Item
                                onClick={(event: React.SyntheticEvent) => onDeleteNode(event)}
                                icon={defaultActionsWithIcon ? mdiTrashCanOutline : undefined}
                                {...get({
                                    element: 'item',
                                    action: 'remove',
                                })}
                            >
                                {translateKey(deleteLabel)}
                            </ActionMenu.Item>
                        )}
                    </ActionMenu>
                )}
                {actions}
            </FlexBox>
        </FlexBox>
    );
};
